import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommandCenter, selectFilteredAssets } from '../store';

type PaletteAction =
  | { kind: 'asset'; id: string; label: string; hint: string }
  | { kind: 'nav'; label: string; hint: string; path: string }
  | { kind: 'cmd'; label: string; hint: string; run: () => void };

export function CommandPalette() {
  const open = useCommandCenter((s) => s.paletteOpen);
  const setOpen = useCommandCenter((s) => s.setPaletteOpen);
  const select = useCommandCenter((s) => s.select);
  const setMode = useCommandCenter((s) => s.setMode);
  const toggleLayer = useCommandCenter((s) => s.toggleLayer);
  const assets = useCommandCenter(selectFilteredAssets);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  // Cmd/Ctrl+K to open
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const meta = isMac ? e.metaKey : e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
      }
      if (open && e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const actions = useMemo<PaletteAction[]>(() => {
    const assetActions: PaletteAction[] = assets.map((a) => ({
      kind: 'asset' as const,
      id: a.id,
      label: a.name,
      hint: `${a.code} · ${a.status}`,
    }));
    const navActions: PaletteAction[] = [
      { kind: 'nav', label: 'Go to Assets', hint: '/assets', path: '/assets' },
      {
        kind: 'nav',
        label: 'Go to Field Users',
        hint: '/field-users',
        path: '/field-users',
      },
      {
        kind: 'nav',
        label: 'Go to Reports',
        hint: '/reports',
        path: '/reports',
      },
      {
        kind: 'nav',
        label: 'Go to Settings',
        hint: '/settings',
        path: '/settings',
      },
    ];
    const cmdActions: PaletteAction[] = [
      {
        kind: 'cmd',
        label: 'Switch to LIVE',
        hint: 'mode',
        run: () => setMode('LIVE'),
      },
      {
        kind: 'cmd',
        label: 'Switch to REPLAY',
        hint: 'mode',
        run: () => setMode('REPLAY'),
      },
      {
        kind: 'cmd',
        label: 'Toggle geofences',
        hint: 'layer',
        run: () => toggleLayer('geofences'),
      },
      {
        kind: 'cmd',
        label: 'Toggle satellite',
        hint: 'layer',
        run: () => toggleLayer('satellite'),
      },
    ];
    return [...assetActions, ...navActions, ...cmdActions];
  }, [assets, setMode, toggleLayer]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions.slice(0, 12);
    return actions
      .filter((a) => a.label.toLowerCase().includes(q) || a.hint.toLowerCase().includes(q))
      .slice(0, 20);
  }, [actions, query]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  if (!open) return null;

  const choose = (a: PaletteAction) => {
    if (a.kind === 'asset') select(a.id);
    if (a.kind === 'nav') navigate(a.path);
    if (a.kind === 'cmd') a.run();
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/60 pt-32"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[560px] border border-cc-border bg-cc-surface font-mono shadow-2xl"
      >
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              setActive((i) => Math.min(filtered.length - 1, i + 1));
              e.preventDefault();
            }
            if (e.key === 'ArrowUp') {
              setActive((i) => Math.max(0, i - 1));
              e.preventDefault();
            }
            if (e.key === 'Enter' && filtered[active]) {
              choose(filtered[active]);
            }
          }}
          placeholder="Type to search assets, jump, run command…"
          className="w-full border-b border-cc-border bg-cc-bg px-3 py-2 text-sm text-cc-fg outline-none"
        />
        <ul className="max-h-80 overflow-auto">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-xs text-cc-muted">No matches</li>
          ) : (
            filtered.map((a, i) => (
              <li
                key={`${a.kind}-${a.label}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(a)}
                className={`flex items-center justify-between px-3 py-2 text-xs ${
                  i === active ? 'bg-cc-accent/20 text-cc-accent' : 'text-cc-fg hover:bg-cc-bg'
                }`}
              >
                <span>{a.label}</span>
                <span className="text-[10px] uppercase text-cc-muted">{a.hint}</span>
              </li>
            ))
          )}
        </ul>
        <p className="border-t border-cc-border px-3 py-1 text-[10px] uppercase text-cc-muted">
          ↑↓ navigate · ↵ open · esc close · ⌘K toggle
        </p>
      </div>
    </div>
  );
}
